import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import { propEq, pipe, find, pathEq } from 'ramda'
import { colors, breakpoints, fontWeights } from '@/styles'
import { isNullOrEmpty } from '@/utils'
import useInterval from '@/hooks/use-interval'
import useSession from '@/hooks/use-current-session'
import useRole from '@/hooks/use-role'
import { showMobileWarning as showMobileWarningModal } from '@/components/mobile-contract-warning-modal'
import { Button } from '@/components/button'
import { Label } from '@/components/text'
import Tooltip from '@/components/popper-tooltip'
import DropdownMenu from './dropdown-menu'
import RecipientsMenu from './recipients-menu'
import Status from './status'
import RedXIcon from './red-x-icon.svg'
import DownloadIcon from './download-icon.svg'
import ErrorIcon from './error-icon.svg'
import ActionButton from './action-button'
import NativeActionButton from './native-action-button'
import { handlePDFView } from './pdf-view'

const isDraft = propEq('state_machine', 'draft')

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${props => (props.header ? 'margin-bottom: 12px;' : 'margin-bottom: 38px;')}
  ${props => (props.primary ? 'margin-bottom: 38px;' : '')}
  ${props => (props.secondary ? 'margin-bottom: 12px;' : '')}
`

const TableTitle = styled.h5`
  font-size: 16px;
  font-weight: 500;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-bottom: 12px;
  }
`

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr 1fr 1fr 100px;
  gap: 16px;
  padding: 16px;
  align-items: center;
`

const TableLabelWrapper = styled(TableGrid)`
  color: ${colors.grey56};
`

const TableContentWapper = styled(TableGrid)`
  color: ${colors.black};
  background: ${colors.white};
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
`

const ClickWrapper = styled.div`
  // lots of "text-decoration: underline" due to weird Firefox bug
  ${props =>
    props.clickableFileName &&
    `
        cursor: pointer;
        text-decoration: underline;
        > div {
          cursor: pointer;
          text-decoration: underline;
          > button {
            display: flex;
            text-decoration: underline;
          }
        }
    `}
`

const MobileTableWrapper = styled.div`
  background: ${colors.white};
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  flex: 1;
`

const MobileTableHeaderContentWapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  font-size: 14px;
  justify-content: flex-start;
  align-items: center;

  width: 100%;
`

const MobileTableBodyContentWrapper = styled.div`
  display: flex;
  width: 100%;
  margin-top: 12px;
  gap: 12px;
`

const MobileTableBodyContentInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  line-height: 16px;
  color: ${colors.grey56};

  p {
    margin: 0;
    color: ${colors.black};
  }
`

const MobileTableContentColumns = styled.div`
  display: flex;
`
const MobileTableContentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const MobileTableButtonContainer = styled(MobileTableContentRow)`
  border-top: 1px solid ${colors.gre88};
  margin-top: 16px;
  padding-top: 16px;
`

const MobileTableContentColumnsFileName = styled.p`
  font-size: 14px;
  line-height: 16px;
  font-weight: 500;
  color: ${colors.black};
  margin: 0;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  overflow-wrap: break-word;
  max-width: 100%;
  // need "display: inline" otherwise Firefox will not show underline
  ${props =>
    props.clickableFileName &&
    `
    cursor: pointer;
    text-decoration: underline;
    display: inline;
    margin-bottom: 1px;
  `}
`

const MobileTableContentDropdownIcon = styled.div``

const StatusWrapper = styled.div`
  display: flex;
  align-items: center;

  div {
    display: flex;
    align-items: center;
  }
`

const TableContentMoreButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
`

const DocumentsIcon = styled.img`
  width: 14px;
  height: 14px;
`
const FullText = styled.div`
  font-weight: ${fontWeights.medium};
  font-size: 14px;
  line-height: 16px;
`

const TooltipWrapper = styled.div`
  div {
    max-width: 300px;
    margin: 0;
    padding: 0;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`

const ContractName = styled.div`
  font-weight: ${fontWeights.medium};
  font-size: 14px;
  line-height: 16px;
  padding-right: 15px;
  width: fit-content;
  max-width: 250px;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${props =>
    props.clickableFileName &&
    `
    text-decoration: underline;
    cursor: pointer;
  `}
`

const ToolTipText = styled(FullText)`
  padding: 0;
  word-wrap: break-word;
`

const MobileFileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  overflow-wrap: break-word;
  max-width: 100%;
`

const MobileContentVerticalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  max-width: 75%;
  text-overflow: ellipsis;
  white-space: normal;
  overflow-wrap: break-word;
  overflow: hidden;
`

const MobileTable = ({
  documentStatus,
  contract,
  packageFile,
  order,
  viewContract,
  packageFiles,
  uploadIncomplete,
  signers = [],
  uploadingStatus,
  handleFileDelete,
  canResend,
  setCanResend,
  countDown,
  countDownLimit,
  handleContractEdit,
  handleFileNameClick,
  clickableFileName
}) => {
  const { t } = useTranslation()
  const role = useRole()
  const isNativeContract = contract?.contract_structure === 'native' ? true : false
  const landlordSigned = pipe(
    find(pathEq(['metadata', 'is_creator'], true)),
    propEq('state_machine', 'signed')
  )(signers)
  const { state_machine } = packageFile
  const isDraft = state_machine === 'draft'
  const isCreated = state_machine === 'created'
  const isSent = state_machine === 'sent'
  const isSigned = state_machine === 'signed'
  const isContractSentNotSigned =
    isSent && packageFile?.signature_status === 'pending' && !landlordSigned

  const showActionButton = isContractSentNotSigned || isCreated
  const handleClick = () => {
    if (isDraft || isCreated) showMobileWarningModal()
    if (isSent || isSigned) handlePDFView(contract?.id, packageFile.id)
  }
  return (
    <MobileTableWrapper onClick={handleClick}>
      <MobileTableHeaderContentWapper>
        <MobileTableContentRow>
          <MobileContentVerticalWrapper>
            <MobileTableContentColumns>
              <MobileFileInfo>
                <MobileTableContentColumnsFileName
                  onClick={handleFileNameClick}
                  clickableFileName={clickableFileName}
                >
                  {isNativeContract
                    ? t(
                        'l.contract_manager.documents.provincial_tenancy_agreement.title',
                        'Provincial Tenancy Agreement'
                      )
                    : packageFile?.file_original_name}
                </MobileTableContentColumnsFileName>
              </MobileFileInfo>
            </MobileTableContentColumns>
            <MobileTableContentColumns>
              {uploadIncomplete ? (
                <MobileTableBodyContentWrapper>
                  <MobileTableBodyContentInner>
                    <div>{uploadingStatus[packageFile.upload_status]}</div>
                  </MobileTableBodyContentInner>
                </MobileTableBodyContentWrapper>
              ) : (
                <MobileTableBodyContentWrapper>
                  <MobileTableBodyContentInner>
                    <div>{documentStatus}</div>
                  </MobileTableBodyContentInner>
                  <MobileTableBodyContentInner>
                    <div>
                      <p>
                        {isNativeContract
                          ? t(
                              'l.contract_manager.documents.regular_contract.title',
                              'Regular Contract'
                            )
                          : t('l.contract_manager.documents.liv_contract.title', 'Liv Contract')}
                      </p>
                    </div>
                  </MobileTableBodyContentInner>
                </MobileTableBodyContentWrapper>
              )}
            </MobileTableContentColumns>
          </MobileContentVerticalWrapper>
          <MobileTableContentColumns>
            <MobileTableContentDropdownIcon>
              {uploadIncomplete ? (
                <ButtonWrapper>
                  <Button theme="text" size="small" space="none" onClick={handleFileDelete}>
                    <DocumentsIcon src={RedXIcon} alt="delete" />
                  </Button>
                </ButtonWrapper>
              ) : (
                <DropdownMenu
                  viewContract={viewContract}
                  isNativeContract={isNativeContract}
                  contractId={contract?.id}
                  isMobile={true}
                  role={role}
                  contract={contract}
                  packageFile={packageFile}
                  packageFiles={packageFiles}
                  canResend={canResend}
                  setCanResend={setCanResend}
                  countDown={countDown}
                  countDownLimit={countDownLimit}
                  handleContractEdit={handleContractEdit}
                />
              )}
            </MobileTableContentDropdownIcon>
          </MobileTableContentColumns>
        </MobileTableContentRow>
        {showActionButton && (
          <MobileTableButtonContainer>
            {isNativeContract ? (
              <NativeActionButton
                contract={contract}
                packageFile={packageFile}
                order={order}
                signers={signers}
              />
            ) : (
              <ActionButton
                contract={contract}
                packageFile={packageFile}
                order={order}
                signers={signers}
                canResend={canResend}
                setCanResend={setCanResend}
                countDown={countDown}
                countDownLimit={countDownLimit}
              />
            )}
          </MobileTableButtonContainer>
        )}
      </MobileTableHeaderContentWapper>
    </MobileTableWrapper>
  )
}

const COUNTDOWN_TIME_LIMIT = 60

const Tables = ({
  title,
  documentStatus,
  signatureStatus,
  header,
  primary,
  secondary,
  isMobile,
  contract,
  packageFile,
  order,
  signers,
  handleDelete,
  packageFiles
}) => {
  const history = useHistory()
  const { t } = useTranslation()
  const session = useSession()

  const role = useRole()
  const isNativeContract = contract?.contract_structure === 'native' ? true : false
  const uploadIncomplete = packageFile.upload_status !== 'success' && !isNativeContract
  const uploadError = packageFile.upload_status === 'error'
  const countDownId = `${session?.id}-contract-${contract?.id}-package-file-${packageFile?.id}-countdown`

  const [canResend, setCanResend] = useState(true)
  const [countDown, setCountDown] = useState(COUNTDOWN_TIME_LIMIT)

  useInterval(
    () => {
      setCountDown(countDown - 1)
      localStorage.setItem(countDownId, countDown - 1)
      if (countDown <= 1) {
        setCanResend(true)
        setCountDown(COUNTDOWN_TIME_LIMIT)
      }
    },
    canResend ? null : 1000
  )

  useEffect(() => {
    const cd = localStorage.getItem(countDownId)
    if (cd && cd > 2) {
      setCountDown(localStorage.getItem(countDownId))
      setCanResend(false)
    }
  }, [countDownId])

  const viewContract = () => {
    const getNewUrl = (role, contract) => {
      const where = role === 'tenant' ? 'rental' : 'listing'

      if (isDraft(contract))
        return `/listing/${contract?.listing_id}/contract/create/${
          contract?.listing_contract_id || ''
        }`
      return `/${where}/${contract?.listing_id}/contract/${contract?.id}`
    }

    history.push(getNewUrl(role, contract))
  }

  const handleFileDelete = () => handleDelete({ contractId: contract?.id, fileId: packageFile?.id })

  const handleContractEdit = (e, file) => {
    e.stopPropagation()
    if (isMobile) {
      showMobileWarningModal()
    } else {
      const link =
        file?.document_type === 'tenancy_agreement'
          ? `/custom-contract/create/${contract?.id}`
          : `/custom-contract/create/${contract?.id}?currentDocument=${file?.id}`

      history.push(link)
    }
  }

  const isContractDraft = packageFile?.state_machine === 'draft'
  const isContractError = packageFile?.state_machine === 'error'
  const isContractUnsent = packageFile?.state_machine === 'created'
  const isContractSentNotSigned =
    packageFile?.state_machine === 'sent' && packageFile?.signature_status === 'pending'
  const isFileSigned = packageFile?.state_machine === 'signed'
  const isContractActive = packageFile?.state_machine === 'active'

  const clickableFileName =
    !uploadError &&
    !uploadIncomplete &&
    (isContractSentNotSigned ||
      isNativeContract ||
      isContractDraft ||
      isContractError ||
      isContractUnsent ||
      isFileSigned ||
      isContractActive)

  const handleFileNameClick = e => {
    e.stopPropagation()
    if (uploadError || uploadIncomplete) return
    if (isContractSentNotSigned || isFileSigned) {
      return handlePDFView(contract?.id, packageFile.id)
    }
    if (isNativeContract || isContractActive) {
      return viewContract()
    }
    if (isContractDraft || isContractError || isContractUnsent) {
      return handleContractEdit(e, packageFile)
    }
  }

  const UPLOADING_STATUS = {
    pending: (
      <Status
        text={t('l.contract_manager.documents.uploading.label', 'Uploading...')}
        backgroundColor={colors.grey96}
        color={colors.grey24}
        icon={<DocumentsIcon src={DownloadIcon} alt="downloading" />}
      />
    ),
    in_progress: (
      <Status
        text={t('l.contract_manager.documents.uploading.label', 'Uploading...')}
        backgroundColor={colors.grey96}
        color={colors.grey24}
        icon={<DocumentsIcon src={DownloadIcon} alt="delete" />}
      />
    ),
    error: (
      <Tooltip
        theme="secondary"
        enabled={true}
        size="medium"
        content={
          <TooltipWrapper>
            <FullText>{packageFile.upload_status_reason}</FullText>
          </TooltipWrapper>
        }
        placement={'top'}
      >
        <Status
          text={`${t('l.contract_manager.documents.error.label', 'Error:')} ${
            packageFile.upload_status_reason
          }`}
          backgroundColor={colors.red88}
          color={colors.red24}
          icon={<DocumentsIcon src={ErrorIcon} alt="downloading" />}
        />
      </Tooltip>
    )
  }

  return (
    <TableWrapper header={header} primary={primary} secondary={secondary}>
      {header === true && (
        <>
          <TableTitle>
            {title}
            {/* Tooltip is not part of v1 */}
            {/* <TableTitleTooltip src={TooltipIcon} alt="tooltip" /> */}
          </TableTitle>

          {!isMobile && (
            <TableLabelWrapper>
              <Label fontSize="14px" type="small">
                {t('l.contract_manager.documents.file.label', 'File')}
              </Label>
              <Label fontSize="14px" type="small">
                {t('l.contract_manager.documents.status.label', 'Status')}
              </Label>
              <Label fontSize="14px" type="small">
                {t('l.contract_manager.documents.type.label', 'Type')}
              </Label>
            </TableLabelWrapper>
          )}
        </>
      )}

      {!isMobile ? (
        <TableContentWapper>
          <ClickWrapper onClick={handleFileNameClick} clickableFileName={clickableFileName}>
            <Tooltip
              theme="secondary"
              enabled={!isNullOrEmpty(packageFile.file_original_name)}
              size="medium"
              content={
                <TooltipWrapper>
                  <ToolTipText>{packageFile.file_original_name}</ToolTipText>
                </TooltipWrapper>
              }
              placement="top"
            >
              <ContractName clickableFileName={clickableFileName}>
                {isNativeContract
                  ? t(
                      'l.contract_manager.documents.provincial_tenancy_agreement.title',
                      'Provincial Tenancy Agreement'
                    )
                  : packageFile.file_original_name}
              </ContractName>
            </Tooltip>
          </ClickWrapper>
          {uploadIncomplete ? (
            <StatusWrapper>{UPLOADING_STATUS[packageFile.upload_status]}</StatusWrapper>
          ) : (
            <>
              <StatusWrapper>{documentStatus}</StatusWrapper>
              <Label fontSize="14px" type="small">
                {isNativeContract
                  ? t('l.contract_manager.documents.regular_contract.title', 'Regular Contract')
                  : t('l.contract_manager.documents.liv_contract.title', 'Liv Contract')}
              </Label>
            </>
          )}

          {uploadIncomplete ? (
            <>
              <div></div>
              <div></div>
              <ButtonWrapper>
                <Button theme="text" size="small" space="none" onClick={handleFileDelete}>
                  <DocumentsIcon src={RedXIcon} alt="delete" />
                </Button>
              </ButtonWrapper>
            </>
          ) : (
            <>
              {isNativeContract ? (
                <NativeActionButton
                  contract={contract}
                  packageFile={packageFile}
                  order={order}
                  signers={signers}
                />
              ) : (
                <ActionButton
                  contract={contract}
                  packageFile={packageFile}
                  order={order}
                  signers={signers}
                  countDown={countDown}
                  countDownLimit={COUNTDOWN_TIME_LIMIT}
                  canResend={canResend}
                  setCanResend={setCanResend}
                />
              )}
              <TableContentMoreButtonWrapper>
                <RecipientsMenu
                  viewContract={viewContract}
                  isNativeContract={isNativeContract}
                  packageFile={packageFile}
                  contract={contract}
                  packageFiles={packageFiles}
                />
                <DropdownMenu
                  viewContract={viewContract}
                  isNativeContract={isNativeContract}
                  contractId={contract?.id}
                  role={role}
                  contract={contract}
                  packageFile={packageFile}
                  countDown={countDown}
                  countDownLimit={COUNTDOWN_TIME_LIMIT}
                  canResend={canResend}
                  setCanResend={setCanResend}
                  packageFiles={packageFiles}
                  handleContractEdit={handleContractEdit}
                />
              </TableContentMoreButtonWrapper>
            </>
          )}
        </TableContentWapper>
      ) : (
        <MobileTable
          documentStatus={documentStatus}
          signatureStatus={signatureStatus}
          contract={contract}
          packageFile={packageFile}
          order={order}
          viewContract={viewContract}
          packageFiles={packageFiles}
          uploadIncomplete={uploadIncomplete}
          signers={signers}
          uploadingStatus={UPLOADING_STATUS}
          handleFileDelete={handleFileDelete}
          countDown={countDown}
          countDownLimit={COUNTDOWN_TIME_LIMIT}
          canResend={canResend}
          setCanResend={setCanResend}
          handleContractEdit={handleContractEdit}
          handleFileNameClick={handleFileNameClick}
          clickableFileName={clickableFileName}
        />
      )}
    </TableWrapper>
  )
}

export default Tables
